import { NEXT_LOCALE_COOKIE_KEY, setLocalCookie } from '@/lang/utils';
import { useAuth, useGuest } from '../state';
import { useCallback, useEffect, useState } from 'react';

import { ILocale } from '@/lang/types';
import { getCookie } from 'cookies-next';
import { locales } from '@/lang/locales';
import shallow from 'zustand/shallow';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useSwitchLang } from './use-switch-lang';

export const useSyncLocalization = () => {
  const { locale: routerLanguage } = useRouter();
  const [guest, setGuest] = useGuest(s => [s.guest, s.setGuest], shallow);
  const [user, setUser] = useAuth(s => [s.user, s.setUser], shallow);
  const [isUpdating, setIsUpdating] = useState(false);

  const queryClient = useQueryClient();

  const { switchLang } = useSwitchLang();

  const updateUserLang = useCallback(
    (language: string) => {
      setIsUpdating(true);
      setUser(prevState => ({
        ...prevState,
        language,
      }));
      setIsUpdating(false);
    },
    [setUser]
  );

  useEffect(() => {
    const cookieLanguage = getCookie(NEXT_LOCALE_COOKIE_KEY);
    const navigatorLanguage = navigator.language.split('-')[0];

    const language = cookieLanguage
      ? routerLanguage
      : navigatorLanguage === locales.AR
      ? locales.AR
      : locales.EN;

    setLocalCookie(language as ILocale);

    if (user) {
      if (user.language?.toLowerCase() !== language?.toLowerCase() && !isUpdating) {
        updateUserLang(language);
      }
    }

    if (guest?.language?.toLowerCase() !== language?.toLowerCase()) {
      setGuest(prevState => {
        const newGuest = prevState ? { ...prevState } : {};
        return {
          ...newGuest,
          language,
        };
      });
    }

    if (!cookieLanguage && navigatorLanguage !== routerLanguage) {
      switchLang(language as ILocale);
    } else {
      queryClient.invalidateQueries();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLanguage, setUser, setGuest, queryClient]);
};
